import { TFunction } from '@spotify-internal/i18n-core';
import { AlbumAvailabilityByTerritory, Patch } from '@hooks/useAlbumInfo/types';
import { AvailabilityStatus } from '@hooks/useAlbumInfo/types';
import {
  MultiTrackDetailsResponse,
  TrackDetails,
} from '@hooks/useTrackInfo/types';

// Maps Atlas region codes to region names
export enum Region {
  AF = 'Africa',
  AS = 'Asia',
  EU = 'Europe',
  NA = 'North America',
  CA = 'Central America',
  SA = 'South America',
}

type TerritoryTrackAvailabilityMap = {
  [trackUri: string]: {
    albumUri: string;
    name: string;
    deliveredStart: string;
    deliveredEnd: string;
    effectiveStart: string;
    effectiveEnd: string;
    status: AvailabilityStatus;
    patches: Patch[];
  };
};

export type EnhancedTerritoryTrackAvailabilityMap = {
  territoryTrackData: TerritoryTrackAvailabilityMap;
  multipleDates: boolean;
  mixedAvailability: boolean;
};

export const getRegionName = (regionCode: string) => {
  return Object.hasOwnProperty.call(Region, regionCode)
    ? Region[regionCode as keyof typeof Region]
    : regionCode;
};

export const translateRegionName = (regionCode: string, t: TFunction) => {
  switch (regionCode.toUpperCase()) {
    case Region.AF.toUpperCase():
      return t(
        'atlas.territories.regions.africa',
        'Africa',
        'Name of the region africa',
      );
    case Region.AS.toUpperCase():
      return t(
        'atlas.territories.regions.asia',
        'Asia',
        'Name of the region Asia',
      );
    case Region.EU.toUpperCase():
      return t(
        'atlas.territories.regions.europe',
        'Europe',
        'Name of the region Europe',
      );
    case Region.NA.toUpperCase():
      return t(
        'atlas.territories.regions.north_america',
        'North America',
        'Name of the region North America',
      );
    case Region.CA.toUpperCase():
      return t(
        'atlas.territories.regions.central_america',
        'Central America',
        'Name of the region Central America',
      );
    case Region.SA.toUpperCase():
      return t(
        'atlas.territories.regions.south_america',
        'South America',
        'Name of the region South America',
      );
    default:
      return regionCode;
  }
};

export const getAllRegionNames = () => {
  return Object.values(Region);
};

// https://gist.github.com/maephisto/9228207
export enum IsoCountries {
  AF = 'Afghanistan',
  AX = 'Aland Islands',
  AL = 'Albania',
  DZ = 'Algeria',
  AS = 'American Samoa',
  AD = 'Andorra',
  AO = 'Angola',
  AI = 'Anguilla',
  AQ = 'Antarctica',
  AG = 'Antigua And Barbuda',
  AR = 'Argentina',
  AM = 'Armenia',
  AW = 'Aruba',
  AU = 'Australia',
  AT = 'Austria',
  AZ = 'Azerbaijan',
  BS = 'Bahamas',
  BH = 'Bahrain',
  BD = 'Bangladesh',
  BB = 'Barbados',
  BY = 'Belarus',
  BE = 'Belgium',
  BZ = 'Belize',
  BJ = 'Benin',
  BM = 'Bermuda',
  BT = 'Bhutan',
  BO = 'Bolivia',
  BA = 'Bosnia And Herzegovina',
  BW = 'Botswana',
  BV = 'Bouvet Island',
  BR = 'Brazil',
  IO = 'British Indian Ocean Territory',
  BN = 'Brunei Darussalam',
  BG = 'Bulgaria',
  BF = 'Burkina Faso',
  BI = 'Burundi',
  KH = 'Cambodia',
  CM = 'Cameroon',
  CA = 'Canada',
  CV = 'Cape Verde',
  KY = 'Cayman Islands',
  CF = 'Central African Republic',
  TD = 'Chad',
  CL = 'Chile',
  CN = 'China',
  CX = 'Christmas Island',
  CC = 'Cocos (Keeling) Islands',
  CO = 'Colombia',
  KM = 'Comoros',
  CG = 'Congo',
  CD = 'Congo, Democratic Republic',
  CK = 'Cook Islands',
  CR = 'Costa Rica',
  CI = "Cote D'Ivoire",
  HR = 'Croatia',
  CU = 'Cuba',
  CW = 'Curaçao',
  CY = 'Cyprus',
  CZ = 'Czech Republic',
  DK = 'Denmark',
  DJ = 'Djibouti',
  DM = 'Dominica',
  DO = 'Dominican Republic',
  EC = 'Ecuador',
  EG = 'Egypt',
  SV = 'El Salvador',
  GQ = 'Equatorial Guinea',
  ER = 'Eritrea',
  EE = 'Estonia',
  ET = 'Ethiopia',
  FK = 'Falkland Islands (Malvinas)',
  FO = 'Faroe Islands',
  FJ = 'Fiji',
  FI = 'Finland',
  FR = 'France',
  GF = 'French Guiana',
  PF = 'French Polynesia',
  TF = 'French Southern Territories',
  GA = 'Gabon',
  GM = 'Gambia',
  GE = 'Georgia',
  DE = 'Germany',
  GH = 'Ghana',
  GI = 'Gibraltar',
  GR = 'Greece',
  GL = 'Greenland',
  GD = 'Grenada',
  GP = 'Guadeloupe',
  GU = 'Guam',
  GT = 'Guatemala',
  GG = 'Guernsey',
  GN = 'Guinea',
  GW = 'Guinea-Bissau',
  GY = 'Guyana',
  HT = 'Haiti',
  HM = 'Heard Island & Mcdonald Islands',
  VA = 'Holy See (Vatican City State)',
  HN = 'Honduras',
  HK = 'Hong Kong',
  HU = 'Hungary',
  IS = 'Iceland',
  IN = 'India',
  ID = 'Indonesia',
  IR = 'Iran, Islamic Republic Of',
  IQ = 'Iraq',
  IE = 'Ireland',
  IM = 'Isle Of Man',
  IL = 'Israel',
  IT = 'Italy',
  JM = 'Jamaica',
  JP = 'Japan',
  JE = 'Jersey',
  JO = 'Jordan',
  KZ = 'Kazakhstan',
  XK = 'Kosovo',
  KE = 'Kenya',
  KI = 'Kiribati',
  KR = 'Korea',
  KW = 'Kuwait',
  KG = 'Kyrgyzstan',
  LA = "Lao People's Democratic Republic",
  LV = 'Latvia',
  LB = 'Lebanon',
  LS = 'Lesotho',
  LR = 'Liberia',
  LY = 'Libyan Arab Jamahiriya',
  LI = 'Liechtenstein',
  LT = 'Lithuania',
  LU = 'Luxembourg',
  MO = 'Macao',
  MK = 'Macedonia',
  MG = 'Madagascar',
  MW = 'Malawi',
  MY = 'Malaysia',
  MV = 'Maldives',
  ML = 'Mali',
  MT = 'Malta',
  MH = 'Marshall Islands',
  MQ = 'Martinique',
  MR = 'Mauritania',
  MU = 'Mauritius',
  YT = 'Mayotte',
  MX = 'Mexico',
  FM = 'Micronesia, Federated States Of',
  MD = 'Moldova',
  MC = 'Monaco',
  MN = 'Mongolia',
  ME = 'Montenegro',
  MS = 'Montserrat',
  MA = 'Morocco',
  MZ = 'Mozambique',
  MM = 'Myanmar',
  NA = 'Namibia',
  NR = 'Nauru',
  NP = 'Nepal',
  NL = 'Netherlands',
  AN = 'Netherlands Antilles',
  NC = 'New Caledonia',
  NZ = 'New Zealand',
  NI = 'Nicaragua',
  NE = 'Niger',
  NG = 'Nigeria',
  NU = 'Niue',
  NF = 'Norfolk Island',
  MP = 'Northern Mariana Islands',
  NO = 'Norway',
  OM = 'Oman',
  PK = 'Pakistan',
  PW = 'Palau',
  PS = 'Palestinian Territories',
  PA = 'Panama',
  PG = 'Papua New Guinea',
  PY = 'Paraguay',
  PE = 'Peru',
  PH = 'Philippines',
  PN = 'Pitcairn',
  PL = 'Poland',
  PT = 'Portugal',
  PR = 'Puerto Rico',
  QA = 'Qatar',
  RE = 'Reunion',
  RO = 'Romania',
  RU = 'Russian Federation',
  RW = 'Rwanda',
  BL = 'Saint Barthelemy',
  SH = 'Saint Helena',
  KN = 'Saint Kitts And Nevis',
  LC = 'Saint Lucia',
  MF = 'Saint Martin',
  PM = 'Saint Pierre And Miquelon',
  VC = 'Saint Vincent And Grenadines',
  WS = 'Samoa',
  SM = 'San Marino',
  ST = 'Sao Tome And Principe',
  SA = 'Saudi Arabia',
  SN = 'Senegal',
  RS = 'Serbia',
  SC = 'Seychelles',
  SL = 'Sierra Leone',
  SG = 'Singapore',
  SK = 'Slovakia',
  SI = 'Slovenia',
  SB = 'Solomon Islands',
  SO = 'Somalia',
  ZA = 'South Africa',
  GS = 'South Georgia And Sandwich Isl.',
  ES = 'Spain',
  LK = 'Sri Lanka',
  SD = 'Sudan',
  SR = 'Suriname',
  SJ = 'Svalbard And Jan Mayen',
  SZ = 'Swaziland',
  SE = 'Sweden',
  CH = 'Switzerland',
  SY = 'Syrian Arab Republic',
  TW = 'Taiwan',
  TJ = 'Tajikistan',
  TZ = 'Tanzania',
  TH = 'Thailand',
  TL = 'Timor-Leste',
  TG = 'Togo',
  TK = 'Tokelau',
  TO = 'Tonga',
  TT = 'Trinidad And Tobago',
  TN = 'Tunisia',
  TR = 'Turkey',
  TM = 'Turkmenistan',
  TC = 'Turks And Caicos Islands',
  TV = 'Tuvalu',
  UG = 'Uganda',
  UA = 'Ukraine',
  AE = 'United Arab Emirates',
  GB = 'United Kingdom',
  US = 'United States',
  UM = 'United States Outlying Islands',
  UY = 'Uruguay',
  UZ = 'Uzbekistan',
  VU = 'Vanuatu',
  VE = 'Venezuela',
  VN = 'Vietnam',
  VG = 'Virgin Islands, British',
  VI = 'Virgin Islands, U.S.',
  WF = 'Wallis And Futuna',
  EH = 'Western Sahara',
  YE = 'Yemen',
  ZM = 'Zambia',
  ZW = 'Zimbabwe',
}

export const getTerritoryName = (territoryCode: string) => {
  return Object.hasOwnProperty.call(IsoCountries, territoryCode)
    ? IsoCountries[territoryCode as keyof typeof IsoCountries]
    : territoryCode;
};

// Groups country codes by Atlas region codes
export const isoCountriesByRegion: Record<string, string[]> = {
  AS: [
    'AE',
    'AF',
    'AM',
    'AZ',
    'BD',
    'BH',
    'BN',
    'BT',
    'CC',
    'CN',
    'CX',
    'CY',
    'GE',
    'HK',
    'ID',
    'IL',
    'IN',
    'IO',
    'IQ',
    'IR',
    'JO',
    'JP',
    'KG',
    'KH',
    'KP',
    'KR',
    'KW',
    'KZ',
    'LA',
    'LB',
    'LK',
    'MM',
    'MN',
    'MO',
    'MV',
    'MY',
    'NP',
    'OM',
    'PH',
    'PK',
    'PS',
    'QA',
    'SA',
    'SG',
    'SY',
    'TH',
    'TJ',
    'TL',
    'TM',
    'TR',
    'TW',
    'UZ',
    'VN',
    'YE',
    'AS',
    'AU',
    'CK',
    'FJ',
    'FM',
    'GU',
    'KI',
    'MH',
    'MP',
    'NC',
    'NF',
    'NR',
    'NU',
    'NZ',
    'PF',
    'PG',
    'PN',
    'PW',
    'SB',
    'TK',
    'TO',
    'TV',
    'UM',
    'VU',
    'WF',
    'WS',
  ],
  EU: [
    'AD',
    'AL',
    'AM',
    'AT',
    'AX',
    'AZ',
    'BA',
    'BE',
    'BG',
    'BY',
    'CH',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FO',
    'FR',
    'GB',
    'GE',
    'GG',
    'GI',
    'GR',
    'HR',
    'HU',
    'IE',
    'IM',
    'IS',
    'IT',
    'JE',
    'KZ',
    'XK',
    'XC',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'MD',
    'ME',
    'MK',
    'MT',
    'NL',
    'NO',
    'PL',
    'PT',
    'RO',
    'RS',
    'RU',
    'SE',
    'SI',
    'SJ',
    'SK',
    'SM',
    'TR',
    'UA',
    'VA',
  ],
  AF: [
    'AO',
    'BF',
    'BI',
    'BJ',
    'BW',
    'CD',
    'CF',
    'CG',
    'CI',
    'CM',
    'CV',
    'DJ',
    'DZ',
    'EG',
    'EH',
    'ER',
    'ET',
    'GA',
    'GH',
    'GM',
    'GN',
    'GQ',
    'GW',
    'KE',
    'KM',
    'LR',
    'LS',
    'LY',
    'MA',
    'MG',
    'ML',
    'MR',
    'MU',
    'MW',
    'MZ',
    'NA',
    'NE',
    'NG',
    'RE',
    'RW',
    'SC',
    'SD',
    'SH',
    'SL',
    'SN',
    'SO',
    'SS',
    'ST',
    'SZ',
    'TD',
    'TG',
    'TN',
    'TZ',
    'UG',
    'YT',
    'ZA',
    'ZM',
    'ZW',
  ],
  NA: [
    'AG',
    'AI',
    'AN',
    'AW',
    'BB',
    'BL',
    'BM',
    'BQ',
    'BS',
    'BZ',
    'CA',
    'CU',
    'CW',
    'DM',
    'DO',
    'GD',
    'GL',
    'GP',
    'HT',
    'JM',
    'KN',
    'KY',
    'LC',
    'MF',
    'MQ',
    'MS',
    'MX',
    'PM',
    'PR',
    'SX',
    'TC',
    'TT',
    'UM',
    'US',
    'VC',
    'VG',
    'VI',
  ],
  CA: ['CR', 'SV', 'GT', 'HN', 'NI', 'PA'],
  SA: [
    'AR',
    'BO',
    'BR',
    'CL',
    'CO',
    'EC',
    'FK',
    'GF',
    'GY',
    'PE',
    'PY',
    'SR',
    'UY',
    'VE',
  ],
};

export const getTerritorysRegionCodes = (countryCode: string): string[] => {
  const regions: string[] = [];
  Object.keys(isoCountriesByRegion).find(key => {
    const territories = isoCountriesByRegion[key];
    if (territories.find(territory => territory === countryCode)) {
      regions.push(key);
    }
  });

  return regions;
};

const evalAllElementEquality = (stringArray: string[]) =>
  stringArray.every((val, _i, arr) => val === arr[0]);

const isTrackAvailabilityMultipleDates = (
  deliveredStartList: string[],
  deliveredEndList: string[],
  effectiveStartList: string[],
  effectiveEndList: string[],
): boolean => {
  const allDeliveredStartDatesEqual =
    evalAllElementEquality(deliveredStartList);
  const allDeliveredEndDatesDatesEqual =
    evalAllElementEquality(deliveredEndList);
  const allEffectiveStartDatesEqual =
    evalAllElementEquality(effectiveStartList);
  const allEffectiveEndDatesDatesEqual =
    evalAllElementEquality(effectiveEndList);
  return !(
    allDeliveredStartDatesEqual &&
    allDeliveredEndDatesDatesEqual &&
    allEffectiveStartDatesEqual &&
    allEffectiveEndDatesDatesEqual
  );
};

const getEnhancedTrackAvailability = (
  trackAvailabilityMap: Map<string, TerritoryTrackAvailabilityMap[]>,
) => {
  const enhancedAvailabilityMap = new Map<
    string,
    EnhancedTerritoryTrackAvailabilityMap
  >();

  trackAvailabilityMap.forEach((trackAvailabilityValue, key) => {
    const deliveredStartArr: string[] = [];
    const deliveredEndArr: string[] = [];
    const effectiveStartArr: string[] = [];
    const effectiveEndArr: string[] = [];
    const statusArr: AvailabilityStatus[] = [];

    trackAvailabilityValue.forEach(value => {
      const territoryValueObj = Object.values(value);

      deliveredStartArr.push(territoryValueObj[0].deliveredEnd);
      deliveredEndArr.push(territoryValueObj[0].deliveredStart);
      effectiveStartArr.push(territoryValueObj[0].effectiveStart);
      effectiveEndArr.push(territoryValueObj[0].effectiveEnd);
      statusArr.push(territoryValueObj[0].status);
    });

    const territoryTrackData: TerritoryTrackAvailabilityMap = {};
    trackAvailabilityValue.flatMap(value => {
      Object.assign(territoryTrackData, value);
    });

    const multipleDates = isTrackAvailabilityMultipleDates(
      deliveredStartArr,
      deliveredEndArr,
      effectiveStartArr,
      effectiveEndArr,
    );
    const mixedAvailability = !evalAllElementEquality(statusArr);

    enhancedAvailabilityMap.set(key, {
      territoryTrackData,
      multipleDates,
      mixedAvailability,
    });
  });
  return enhancedAvailabilityMap;
};

export const getTrackAvailabilityByTerritory = (
  fetchedTrackAvailability: MultiTrackDetailsResponse[],
  albumUri: string,
) => {
  const trackAvailabilityMap = new Map<
    string,
    TerritoryTrackAvailabilityMap[]
  >();

  fetchedTrackAvailability.map(trackDetails => {
    const effectiveTrackData: TrackDetails = trackDetails?.effectiveData;
    const availabilityTrackData: AlbumAvailabilityByTerritory =
      trackDetails?.availability;

    if (availabilityTrackData && effectiveTrackData) {
      const territoryKey = Object.keys(availabilityTrackData);

      territoryKey.forEach(territoryCode => {
        const regionKeyArr = getTerritorysRegionCodes(territoryCode).filter(
          regionKey => Object.keys(Region).includes(regionKey),
        );

        regionKeyArr.forEach(regionCode => {
          const territoryRegionCode = `${territoryCode}-${regionCode}`;
          if (trackAvailabilityMap?.get(territoryRegionCode)) {
            const updatedTrackAvailabilityValue = {
              [effectiveTrackData?.uri.replace('spotify:track:', '')]: {
                albumUri,
                name: effectiveTrackData?.name,
                deliveredStart:
                  availabilityTrackData[territoryCode].deliveredStart,
                deliveredEnd: availabilityTrackData[territoryCode].deliveredEnd,
                effectiveStart:
                  availabilityTrackData[territoryCode].effectiveStart,
                effectiveEnd: availabilityTrackData[territoryCode].effectiveEnd,
                status: availabilityTrackData[territoryCode].status,
                patches: availabilityTrackData[territoryCode].patches,
              },
            };

            trackAvailabilityMap.set(territoryRegionCode, [
              ...trackAvailabilityMap?.get(territoryRegionCode)!,
              updatedTrackAvailabilityValue,
            ]);
          } else {
            const newTrackAvailabilityValue = {
              [effectiveTrackData?.uri.replace('spotify:track:', '')]: {
                albumUri,
                name: effectiveTrackData?.name,
                deliveredStart:
                  availabilityTrackData[territoryCode].deliveredStart,
                deliveredEnd: availabilityTrackData[territoryCode].deliveredEnd,
                effectiveStart:
                  availabilityTrackData[territoryCode].effectiveStart,
                effectiveEnd: availabilityTrackData[territoryCode].effectiveEnd,
                status: availabilityTrackData[territoryCode].status,
                patches: availabilityTrackData[territoryCode].patches,
              },
            };
            trackAvailabilityMap.set(territoryRegionCode, [
              newTrackAvailabilityValue,
            ]);
          }
        });
      });
    }
  });
  return getEnhancedTrackAvailability(trackAvailabilityMap);
};
